import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { InvoiceDetailResponse, NexDetailLine } from '../../../types/invoice';
import {
    arrayMove,
    buildLineItemExpandKey,
    remapCurrentLineIndexAfterReorder,
} from '../invoiceDetailFormMappings';
import type { ReconciliationStateForForm } from './invoiceDetailFormTypes';

export interface UseInvoiceDetailFormReorderParams {
    invoice: InvoiceDetailResponse | undefined;
    editedFields: Partial<InvoiceDetailResponse>;
    getReconciliation: () => ReconciliationStateForForm | undefined;
    setEditedFields: Dispatch<SetStateAction<Partial<InvoiceDetailResponse>>>;
    setEditingNumericField: Dispatch<SetStateAction<string | null>>;
    setEditingNumericValues: Dispatch<SetStateAction<Record<string, string>>>;
    setManualPoHeaderSelections: Dispatch<SetStateAction<Record<number, number>>>;
    reviseLineBackendMap: (
        nextItems: InvoiceDetailResponse['lineItems'],
        revise: (prev: readonly (number | undefined)[]) => (number | undefined)[],
    ) => void;
}

export function useInvoiceDetailFormReorder({
    invoice,
    editedFields,
    getReconciliation,
    setEditedFields,
    setEditingNumericField,
    setEditingNumericValues,
    setManualPoHeaderSelections,
    reviseLineBackendMap,
}: UseInvoiceDetailFormReorderParams) {
    const reorderLineItems = useCallback(
        (from: number, to: number) => {
            const currentLineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
            const n = currentLineItems.length;
            if (from < 0 || to < 0 || from >= n || to >= n || from === to) return;
            const reordered = arrayMove(currentLineItems, from, to);
            const mapRow = (i: number) => remapCurrentLineIndexAfterReorder(from, to, i);

            const recon = getReconciliation();
            if (recon) {
                recon.setUnmatchedPartIndices((prev) => {
                    const next = new Set<number>();
                    prev.forEach((idx) => next.add(mapRow(idx)));
                    return next;
                });
                recon.setLocalMatchedNexOverrides((prev) =>
                    Object.fromEntries(
                        Object.entries(prev).map(([idxStr, nexLine]) => {
                            const idx = parseInt(idxStr, 10);
                            const nextIdx = Number.isNaN(idx) ? idxStr : String(mapRow(idx));
                            return [nextIdx, nexLine];
                        })
                    )
                );
                recon.setExpandedMatchedNexRows((prev) => {
                    const next = new Set<string>();
                    for (const key of Array.from(prev)) {
                        const pipe = key.indexOf('|');
                        if (pipe <= 0) {
                            next.add(key);
                            continue;
                        }
                        const oldIdx = parseInt(key.slice(0, pipe), 10);
                        if (Number.isNaN(oldIdx)) {
                            next.add(key);
                            continue;
                        }
                        const newIdx = mapRow(oldIdx);
                        const item = reordered[newIdx];
                        next.add(buildLineItemExpandKey(newIdx, item));
                    }
                    return next;
                });
            }

            setEditingNumericValues((prev) => {
                const next = { ...prev };
                const rewrites: Array<[string, string]> = [];
                for (const k of Object.keys(next)) {
                    const m = k.match(/^lineItem-(\d+)-(.+)$/);
                    if (!m) continue;
                    const oldR = parseInt(m[1], 10);
                    const newR = mapRow(oldR);
                    if (newR !== oldR) rewrites.push([k, `lineItem-${newR}-${m[2]}`]);
                }
                for (const [oldK, newK] of rewrites) {
                    next[newK] = next[oldK]!;
                    delete next[oldK];
                }
                return next;
            });
            setEditingNumericField((prev) => {
                if (prev == null) return prev;
                const m = prev.match(/^lineItem-(\d+)-(.+)$/);
                if (!m) return prev;
                const newR = mapRow(parseInt(m[1], 10));
                return `lineItem-${newR}-${m[2]}`;
            });

            setManualPoHeaderSelections((prev) => {
                const next: Record<number, number> = {};
                for (const [kStr, v] of Object.entries(prev)) {
                    const k = parseInt(kStr, 10);
                    if (Number.isNaN(k) || typeof v !== 'number' || !Number.isFinite(v)) continue;
                    if (k >= 0 && k < n) next[mapRow(k)] = v;
                    else next[k] = v;
                }
                return next;
            });

            reviseLineBackendMap(reordered, (map) => arrayMove(map, from, to));
            setEditedFields((prev) => ({ ...prev, lineItems: reordered }));
        },
        [
            editedFields.lineItems,
            invoice?.lineItems,
            getReconciliation,
            setEditedFields,
            setEditingNumericField,
            setEditingNumericValues,
            setManualPoHeaderSelections,
            reviseLineBackendMap,
        ]
    );

    const reorderFreightItems = useCallback(
        (from: number, to: number) => {
            const lineItemsArr = editedFields.lineItems ?? invoice?.lineItems ?? [];
            const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
            const L = lineItemsArr.length;
            const n = freightItems.length;
            if (from < 0 || to < 0 || from >= n || to >= n || from === to) return;
            const reordered = arrayMove(freightItems, from, to);
            const mapLocal = (i: number) => remapCurrentLineIndexAfterReorder(from, to, i);
            const mapGlobal = (g: number) => {
                if (g >= L && g < L + n) return L + mapLocal(g - L);
                return g;
            };

            const recon = getReconciliation();
            if (recon) {
                recon.setUnmatchedPartIndices((prev) => {
                    const next = new Set<number>();
                    prev.forEach((idx) => next.add(mapGlobal(idx)));
                    return next;
                });
                recon.setLocalMatchedNexOverrides((prev) =>
                    Object.fromEntries(
                        Object.entries(prev).map(([idxStr, nexLine]) => {
                            const idx = parseInt(idxStr, 10);
                            const nextIdx = Number.isNaN(idx) ? idxStr : String(mapGlobal(idx));
                            return [nextIdx, nexLine];
                        })
                    )
                );
                recon.setExpandedMatchedNexRows((prev) => {
                    const next = new Set<string>();
                    for (const key of Array.from(prev)) {
                        const fm = /^freight-nex-(\d+)-(\d+)$/.exec(key);
                        if (fm) {
                            const lineLen = parseInt(fm[1], 10);
                            const localIdx = parseInt(fm[2], 10);
                            if (lineLen === L && localIdx >= 0 && localIdx < n) {
                                next.add(`freight-nex-${L}-${mapLocal(localIdx)}`);
                                continue;
                            }
                        }
                        next.add(key);
                    }
                    return next;
                });
            }

            setEditingNumericValues((prev) => {
                const next = { ...prev };
                const rewrites: Array<[string, string]> = [];
                for (const k of Object.keys(next)) {
                    const m = k.match(/^freightItem-(\d+)-(.+)$/);
                    if (!m) continue;
                    const oldR = parseInt(m[1], 10);
                    const newR = mapLocal(oldR);
                    if (newR !== oldR) rewrites.push([k, `freightItem-${newR}-${m[2]}`]);
                }
                for (const [oldK, newK] of rewrites) {
                    next[newK] = next[oldK]!;
                    delete next[oldK];
                }
                return next;
            });
            setEditingNumericField((prev) => {
                if (prev == null) return prev;
                const m = prev.match(/^freightItem-(\d+)-(.+)$/);
                if (!m) return prev;
                return `freightItem-${mapLocal(parseInt(m[1], 10))}-${m[2]}`;
            });
            setManualPoHeaderSelections((prev) => {
                const next: Record<number, number> = {};
                for (const [kStr, v] of Object.entries(prev)) {
                    const k = parseInt(kStr, 10);
                    if (Number.isNaN(k) || typeof v !== 'number' || !Number.isFinite(v)) continue;
                    if (k >= L && k < L + n) next[mapGlobal(k)] = v;
                    else next[k] = v;
                }
                return next;
            });

            setEditedFields((prev) => ({ ...prev, freightItems: reordered }));
        },
        [
            editedFields.lineItems,
            editedFields.freightItems,
            invoice?.lineItems,
            invoice?.freightItems,
            getReconciliation,
            setEditedFields,
            setEditingNumericField,
            setEditingNumericValues,
            setManualPoHeaderSelections,
        ]
    );

    const reorderRestockingFeeItems = useCallback(
        (from: number, to: number) => {
            const lineItemsArr = editedFields.lineItems ?? invoice?.lineItems ?? [];
            const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
            const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
            const restockingItems = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
            const L = lineItemsArr.length;
            const F = freightItems.length;
            const Fu = fuelItems.length;
            const n = restockingItems.length;
            const base = L + F + Fu;
            if (from < 0 || to < 0 || from >= n || to >= n || from === to) return;
            const reordered = arrayMove(restockingItems, from, to);
            const mapLocal = (i: number) => remapCurrentLineIndexAfterReorder(from, to, i);
            const mapGlobal = (g: number) => {
                if (g >= base && g < base + n) return base + mapLocal(g - base);
                return g;
            };

            const recon = getReconciliation();
            if (recon) {
                recon.setUnmatchedPartIndices((prev) => {
                    const next = new Set<number>();
                    prev.forEach((idx) => next.add(mapGlobal(idx)));
                    return next;
                });
                recon.setLocalMatchedNexOverrides((prev) =>
                    Object.fromEntries(
                        Object.entries(prev).map(([idxStr, nexLine]) => {
                            const idx = parseInt(idxStr, 10);
                            const nextIdx = Number.isNaN(idx) ? idxStr : String(mapGlobal(idx));
                            return [nextIdx, nexLine];
                        })
                    )
                );
                recon.setExpandedMatchedNexRows((prev) => {
                    const next = new Set<string>();
                    for (const key of Array.from(prev)) {
                        const mm = /^misc-nex-(\d+)-(\d+)-(\d+)-(\d+)$/.exec(key);
                        if (mm) {
                            const lL = parseInt(mm[1], 10);
                            const lF = parseInt(mm[2], 10);
                            const lFu = parseInt(mm[3], 10);
                            const loc = parseInt(mm[4], 10);
                            if (lL === L && lF === F && lFu === Fu && loc >= 0 && loc < n) {
                                next.add(`misc-nex-${L}-${F}-${Fu}-${mapLocal(loc)}`);
                                continue;
                            }
                        }
                        next.add(key);
                    }
                    return next;
                });
            }

            setEditingNumericValues((prev) => {
                const next = { ...prev };
                const rewrites: Array<[string, string]> = [];
                for (const k of Object.keys(next)) {
                    const m = k.match(/^restockingFeeItem-(\d+)-(.+)$/);
                    if (!m) continue;
                    const oldR = parseInt(m[1], 10);
                    const newR = mapLocal(oldR);
                    if (newR !== oldR) rewrites.push([k, `restockingFeeItem-${newR}-${m[2]}`]);
                }
                for (const [oldK, newK] of rewrites) {
                    next[newK] = next[oldK]!;
                    delete next[oldK];
                }
                return next;
            });
            setEditingNumericField((prev) => {
                if (prev == null) return prev;
                const m = prev.match(/^restockingFeeItem-(\d+)-(.+)$/);
                if (!m) return prev;
                return `restockingFeeItem-${mapLocal(parseInt(m[1], 10))}-${m[2]}`;
            });
            setManualPoHeaderSelections((prev) => {
                const next: Record<number, number> = {};
                for (const [kStr, v] of Object.entries(prev)) {
                    const k = parseInt(kStr, 10);
                    if (Number.isNaN(k) || typeof v !== 'number' || !Number.isFinite(v)) continue;
                    if (k >= base && k < base + n) next[mapGlobal(k)] = v;
                    else next[k] = v;
                }
                return next;
            });

            setEditedFields((prev) => ({ ...prev, restockingFeeItems: reordered }));
        },
        [
            editedFields.lineItems,
            editedFields.freightItems,
            editedFields.fuelItems,
            editedFields.restockingFeeItems,
            invoice?.lineItems,
            invoice?.freightItems,
            invoice?.fuelItems,
            invoice?.restockingFeeItems,
            getReconciliation,
            setEditedFields,
            setEditingNumericField,
            setEditingNumericValues,
            setManualPoHeaderSelections,
        ]
    );

    const reorderIgnoredParts = useCallback(
        (from: number, to: number) => {
            const lineItemsArr = editedFields.lineItems ?? invoice?.lineItems ?? [];
            const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
            const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
            const restockingItems = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
            const discountItems = editedFields.discountItems ?? invoice?.discountItems ?? [];
            const ignoredList =
                editedFields.ignoredParts ??
                invoice?.ignoredParts ??
                (invoice != null && 'canceledParts' in invoice
                    ? (invoice as { canceledParts?: InvoiceDetailResponse['ignoredParts'] }).canceledParts
                    : undefined) ??
                [];
            const L = lineItemsArr.length;
            const F = freightItems.length;
            const Fu = fuelItems.length;
            const R = restockingItems.length;
            const D = discountItems.length;
            const n = ignoredList.length;
            const base = L + F + Fu + R + D;
            if (from < 0 || to < 0 || from >= n || to >= n || from === to) return;
            const reordered = arrayMove(ignoredList, from, to);
            const mapLocal = (i: number) => remapCurrentLineIndexAfterReorder(from, to, i);
            const mapGlobal = (g: number) => {
                if (g >= base && g < base + n) return base + mapLocal(g - base);
                return g;
            };

            const currentIssues = invoice?.reconciliationIssues as
                | { matchedNexLinesForIgnored?: Array<{ ignoredIndex: number; nexLine: NexDetailLine }> }
                | undefined;
            const priorEditedRecon = editedFields.reconciliationIssues as typeof currentIssues | undefined;
            const baseIgnoredMatches =
                priorEditedRecon?.matchedNexLinesForIgnored ?? currentIssues?.matchedNexLinesForIgnored ?? [];
            const remappedIgnoredMatches =
                baseIgnoredMatches.length > 0
                    ? baseIgnoredMatches.map((row) => ({
                          ...row,
                          ignoredIndex: mapLocal(row.ignoredIndex),
                      }))
                    : undefined;

            const recon = getReconciliation();
            if (recon) {
                recon.setUnmatchedPartIndices((prev) => {
                    const next = new Set<number>();
                    prev.forEach((idx) => next.add(mapGlobal(idx)));
                    return next;
                });
                recon.setLocalMatchedNexOverrides((prev) =>
                    Object.fromEntries(
                        Object.entries(prev).map(([idxStr, nexLine]) => {
                            const idx = parseInt(idxStr, 10);
                            const nextIdx = Number.isNaN(idx) ? idxStr : String(mapGlobal(idx));
                            return [nextIdx, nexLine];
                        })
                    )
                );
                recon.setExpandedMatchedNexIgnoredRows((prev: Set<number>) => {
                    const next = new Set<number>();
                    prev.forEach((idx: number) => next.add(mapLocal(idx)));
                    return next;
                });
            }

            setEditingNumericValues((prev) => {
                const next = { ...prev };
                const rewrites: Array<[string, string]> = [];
                for (const k of Object.keys(next)) {
                    const m = k.match(/^ignoredPart-(\d+)-(.+)$/);
                    if (!m) continue;
                    const oldR = parseInt(m[1], 10);
                    const newR = mapLocal(oldR);
                    if (newR !== oldR) rewrites.push([k, `ignoredPart-${newR}-${m[2]}`]);
                }
                for (const [oldK, newK] of rewrites) {
                    next[newK] = next[oldK]!;
                    delete next[oldK];
                }
                return next;
            });
            setEditingNumericField((prev) => {
                if (prev == null) return prev;
                const m = prev.match(/^ignoredPart-(\d+)-(.+)$/);
                if (!m) return prev;
                return `ignoredPart-${mapLocal(parseInt(m[1], 10))}-${m[2]}`;
            });
            setManualPoHeaderSelections((prev) => {
                const next: Record<number, number> = {};
                for (const [kStr, v] of Object.entries(prev)) {
                    const k = parseInt(kStr, 10);
                    if (Number.isNaN(k) || typeof v !== 'number' || !Number.isFinite(v)) continue;
                    if (k >= base && k < base + n) next[mapGlobal(k)] = v;
                    else next[k] = v;
                }
                return next;
            });

            setEditedFields((prev) => ({
                ...prev,
                ignoredParts: reordered,
                ...(remappedIgnoredMatches != null && remappedIgnoredMatches.length > 0
                    ? {
                          reconciliationIssues: {
                              ...(prev.reconciliationIssues ?? {}),
                              matchedNexLinesForIgnored: remappedIgnoredMatches,
                          } as InvoiceDetailResponse['reconciliationIssues'],
                      }
                    : {}),
            }));
        },
        [
            editedFields.lineItems,
            editedFields.freightItems,
            editedFields.fuelItems,
            editedFields.restockingFeeItems,
            editedFields.discountItems,
            editedFields.ignoredParts,
            editedFields.reconciliationIssues,
            invoice,
            getReconciliation,
            setEditedFields,
            setEditingNumericField,
            setEditingNumericValues,
            setManualPoHeaderSelections,
        ]
    );

    return {
        reorderLineItems,
        reorderFreightItems,
        reorderRestockingFeeItems,
        reorderIgnoredParts,
    };
}
